import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  Tabs, 
  Row, 
  Col, 
  Descriptions, 
  Timeline, 
  Statistic, 
  Tag, 
  Table, 
  Tree, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space, 
  Divider,
  message,
  Spin,
  Empty,
  Progress,
  Badge,
  Popover
} from 'antd'
import { 
  useParams, 
  useNavigate 
} from 'react-router-dom'
import { 
  ClockCircleOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  FileTextOutlined,
  ExpandAltOutlined,
  CompressOutlined,
  DownloadOutlined,
  ReloadOutlined,
  WarningOutlined,
  SearchOutlined,
  FilterOutlined,
  CalendarOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  UserOutlined
} from '@ant-design/icons'
import { 
  PlanogramPlan, 
  Milestone,
  Subtask
} from '../../types'
import planService from '../../services/planService'
import StatusTag from '../../components/common/StatusTag'
import ProgressBar from './components/ProgressBar'
import { useTranslation } from 'react-i18next'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

interface PlanDetailData {
  plan: PlanogramPlan
  milestones: Milestone[]
}

const PlanDetail: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [planData, setPlanData] = useState<PlanDetailData | null>(null)
  const [activeTab, setActiveTab] = useState('milestones')
  
  // Subtask filters
  const [subtaskFilters, setSubtaskFilters] = useState({
    milestone: '',
    assignee: '',
    status: '',
    timeRange: null as [any, any] | null,
    keyword: ''
  })
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const [allExpanded, setAllExpanded] = useState(false)

  useEffect(() => {
    if (id) {
      fetchPlanDetails()
    }
  }, [id])

  const fetchPlanDetails = async () => {
    try {
      setLoading(true)
      const response = await planService.getPlanById(id);
      
      if (response.success && response.data) {
        // Get milestones for this plan
        const milestonesResponse = await planService.getMilestonesByPlan(id);
        const planMilestones = milestonesResponse.success ? milestonesResponse.data : [];
        
        const planDetailData: PlanDetailData = {
          plan: response.data,
          milestones: planMilestones
        }
        
        setPlanData(planDetailData)
        // Auto-expand first level of subtask tree
        const milestoneIds = planMilestones.map((m: Milestone) => m.milestoneId)
        setExpandedKeys(milestoneIds)
      } else {
        message.error(response.message || t('planManagement.detail.messages.fetchFailed'))
      }
    } catch (error: any) {
      message.error(error.message || t('planManagement.detail.messages.fetchFailedRetry'))
    } finally {
      setLoading(false)
    }
  }

  const calculateMilestoneProgress = () => {
    if (!planData) return { completed: 0, total: 0, percentage: 0 }
    const total = planData.milestones.length
    const completed = planData.milestones.filter(m => m.status === 'Completed').length
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
    return { completed, total, percentage }
  }

  const calculateSubtaskProgress = () => {
    if (!planData) return { completed: 0, total: 0, percentage: 0 }
    const allSubtasks = planData.milestones.flatMap(m => m.subtasks || [])
    const total = allSubtasks.length
    const completed = allSubtasks.filter(s => s.status === 'Finished').length
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
    return { completed, total, percentage }
  }

  const getMilestoneStatistics = () => {
    if (!planData) return { notStarted: 0, inProgress: 0, completed: 0, overdue: 0 }
    
    const stats = { notStarted: 0, inProgress: 0, completed: 0, overdue: 0 }
    planData.milestones?.forEach(milestone => {
      switch (milestone.status) {
        case 'NotStarted':
          stats.notStarted++
          break
        case 'InProgress':
          stats.inProgress++
          break
        case 'Completed':
          stats.completed++
          break
        case 'Overdue':
          stats.overdue++
          break
      }
    })
    return stats
  }

  const renderMilestoneTimeline = () => {
    if (!planData) return null

    const sortedMilestones = planData.milestones
      .sort((a: Milestone, b: Milestone) => a.milestoneSequence - b.milestoneSequence)

    // 检查计划是否未开始
    const isPlanNotStarted = planData.plan.status === 'Pending'

    return (
      <div>
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '16px', fontWeight: 500 }}>{t('planManagement.detail.milestones.timeline.title')}</Text>
        </div>
        
        <div style={{ 
          overflowX: 'auto', 
          padding: '20px 0',
          minHeight: '200px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            minWidth: `${sortedMilestones.length * 280}px`,
            position: 'relative',
            justifyContent: 'center',
            gap: '20px'
          }}>
            {sortedMilestones.map((milestone: Milestone, index: number) => {
              let dotColor, cardBorderColor, textColor, bgColor
              
              if (isPlanNotStarted) {
                dotColor = '#d9d9d9'
                cardBorderColor = '#f0f0f0'
                textColor = '#bfbfbf'
                bgColor = '#fafafa'
              } else {
                switch (milestone.status) {
                  case 'Completed':
                    dotColor = '#52c41a'
                    cardBorderColor = '#b7eb8f'
                    textColor = '#52c41a'
                    bgColor = '#f6ffed'
                    break
                  case 'InProgress':
                    dotColor = '#1890ff'
                    cardBorderColor = '#91d5ff'
                    textColor = '#1890ff'
                    bgColor = '#f0f9ff'
                    break
                  case 'Overdue':
                    dotColor = '#ff4d4f'
                    cardBorderColor = '#ffccc7'
                    textColor = '#ff4d4f'
                    bgColor = '#fff2f0'
                    break
                  default:
                    dotColor = '#d9d9d9'
                    cardBorderColor = '#f0f0f0'
                    textColor = '#8c8c8c'
                    bgColor = '#fafafa'
                }
              }

              return (
                <div key={milestone.milestoneId} style={{ position: 'relative' }}>
                  {/* 连接线 */}
                  {index < sortedMilestones.length - 1 && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      right: '-30px',
                      width: '40px',
                      height: '2px',
                      background: `repeating-linear-gradient(
                        to right,
                        ${dotColor} 0,
                        ${dotColor} 4px,
                        transparent 4px,
                        transparent 8px
                      )`,
                      zIndex: 1,
                      transform: 'translateY(-1px)'
                    }} />
                  )}
                  
                  {/* 里程碑卡片 */}
                  <div style={{
                    width: '240px',
                    height: '160px',
                    backgroundColor: bgColor,
                    border: `2px solid ${cardBorderColor}`,
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    position: 'relative',
                    zIndex: 2,
                    transition: 'all 0.3s ease'
                  }}>
                    {/* 日期 */}
                    <div style={{ textAlign: 'center' }}>
                      <Text style={{ 
                        fontSize: '14px', 
                        fontWeight: 600,
                        color: textColor,
                        display: 'block',
                        marginBottom: '4px'
                      }}>
                        {new Date(milestone.plannedStartTime).toLocaleDateString(t('common.locale', 'zh-CN'), {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Text>
                      <Text style={{ 
                        fontSize: '12px',
                        color: textColor,
                        opacity: 0.8
                      }}>
                        M{milestone.milestoneSequence}
                      </Text>
                    </div>

                    {/* 圆形节点 */}
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: dotColor,
                      border: `3px solid white`,
                      boxShadow: `0 0 0 2px ${dotColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '8px 0'
                    }}>
                      {milestone.status === 'Completed' && (
                        <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                      )}
                      {milestone.status === 'InProgress' && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          animation: 'pulse 2s infinite'
                        }} />
                      )}
                    </div>

                    {/* 里程碑名称 */}
                    <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Text style={{ 
                        fontSize: '13px',
                        fontWeight: 500,
                        color: textColor,
                        lineHeight: '1.4',
                        textAlign: 'center',
                        wordBreak: 'break-word'
                      }}>
                        {milestone.milestoneName}
                      </Text>
                      
                      {/* 状态标签 */}
                      <div style={{ marginTop: '8px' }}>
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          backgroundColor: dotColor,
                          color: 'white',
                          fontWeight: 500
                        }}>
                          {isPlanNotStarted ? t('planManagement.detail.milestones.status.NotStarted') :
                           milestone.status === 'Completed' ? t('planManagement.detail.milestones.status.Completed') : 
                           milestone.status === 'InProgress' ? t('planManagement.detail.milestones.status.InProgress') : 
                           milestone.status === 'Overdue' ? t('planManagement.detail.milestones.status.Overdue') : t('planManagement.detail.milestones.status.NotStarted')}
                        </span>
                        
                        {milestone.finalMilestoneFlag && (
                          <div style={{ marginTop: '4px' }}>
                            <Tag color="gold" style={{ fontSize: '10px', padding: '0 6px' }}>{t('planManagement.detail.milestones.timeline.finalMilestone')}</Tag>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        
        {/* 图例 */}
        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <Space size="large">
            <span style={{ fontSize: '12px' }}>
              <span style={{ 
                display: 'inline-block',
                width: '12px', 
                height: '12px', 
                borderRadius: '50%',
                backgroundColor: '#52c41a',
                marginRight: '6px',
                verticalAlign: 'middle'
              }}></span>
              {t('planManagement.detail.milestones.legend.completed')}
            </span>
            <span style={{ fontSize: '12px' }}>
              <span style={{ 
                display: 'inline-block',
                width: '12px', 
                height: '12px', 
                borderRadius: '50%',
                backgroundColor: '#1890ff',
                marginRight: '6px',
                verticalAlign: 'middle'
              }}></span>
              {t('planManagement.detail.milestones.legend.inProgress')}
            </span>
            <span style={{ fontSize: '12px' }}>
              <span style={{ 
                display: 'inline-block',
                width: '12px', 
                height: '12px', 
                borderRadius: '50%',
                backgroundColor: '#d9d9d9',
                marginRight: '6px',
                verticalAlign: 'middle'
              }}></span>
              {t('planManagement.detail.milestones.legend.notStarted')}
            </span>
            <span style={{ fontSize: '12px' }}>
              <span style={{ 
                display: 'inline-block',
                width: '12px', 
                height: '12px', 
                borderRadius: '50%',
                backgroundColor: '#ff4d4f',
                marginRight: '6px',
                verticalAlign: 'middle'
              }}></span>
              {t('planManagement.detail.milestones.legend.overdue')}
            </span>
          </Space>
        </div>

        <style>{`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}</style>
      </div>
    )
  }

  const milestoneColumns = [
    {
      title: t('planManagement.detail.milestones.table.columns.sequence'),
      dataIndex: 'milestoneSequence',
      key: 'sequence',
      width: 80,
      sorter: (a: Milestone, b: Milestone) => a.milestoneSequence - b.milestoneSequence
    },
    {
      title: t('planManagement.detail.milestones.table.columns.milestoneName'),
      dataIndex: 'milestoneName',
      key: 'name',
      render: (text: string, record: Milestone) => (
        <div>
          <Text strong>{text}</Text>
          {record.finalMilestoneFlag && (
            <Tag color="gold" style={{ marginLeft: 8 }}>{t('planManagement.detail.milestones.timeline.finalMilestone')}</Tag>
          )}
        </div>
      )
    },
    {
      title: t('planManagement.detail.milestones.table.columns.department'),
      dataIndex: 'responsibleDepartment',
      key: 'department'
    },
    {
      title: t('planManagement.detail.milestones.table.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <StatusTag type="milestone" status={status} />
    },
    {
      title: t('planManagement.detail.milestones.table.columns.plannedStartTime'),
      key: 'plannedTime',
      render: (record: Milestone) => (
        <div>
          <div>{new Date(record.plannedStartTime).toLocaleDateString()}</div>
          <div>{new Date(record.plannedEndTime).toLocaleDateString()}</div>
        </div>
      )
    },
    {
      title: t('planManagement.detail.milestones.table.columns.actualStartTime'),
      key: 'actualTime',
      render: (record: Milestone) => (
        <div>
          <div>{record.actualStartTime ? new Date(record.actualStartTime).toLocaleDateString() : '-'}</div>
          <div>{record.actualEndTime ? new Date(record.actualEndTime).toLocaleDateString() : '-'}</div>
        </div>
      )
    },
    {
      title: t('planManagement.detail.milestones.table.columns.progress'),
      key: 'progress',
      render: (record: Milestone) => {
        const subtasks = record.subtasks || []
        const total = subtasks.length
        const completed = subtasks.filter(s => s.status === 'Completed').length
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
        
        return (
          <div style={{ width: 140, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <ProgressBar 
                completed={completed}
                total={total}
                size="small"
                showText={false}
              />
            </div>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {completed}/{total} ({percentage}%)
            </Text>
          </div>
        )
      }
    }
  ]

  const getFilteredSubtasks = () => {
    if (!planData) return []
    
    let allSubtasks = planData.milestones?.flatMap((milestone: Milestone) => 
      milestone.subtasks?.map((subtask: any) => ({
        ...subtask,
        milestoneName: milestone.milestoneName
      })) || []
    ) || []

    // Apply filters
    if (subtaskFilters.milestone) {
      allSubtasks = allSubtasks.filter((s: any) => s.milestoneId === subtaskFilters.milestone)
    }
    if (subtaskFilters.assignee) {
      allSubtasks = allSubtasks.filter((s: any) => s.assignee.includes(subtaskFilters.assignee))
    }
    if (subtaskFilters.status) {
      allSubtasks = allSubtasks.filter((s: any) => s.status === subtaskFilters.status)
    }
    if (subtaskFilters.keyword) {
      const keyword = subtaskFilters.keyword.toLowerCase()
      allSubtasks = allSubtasks.filter((s: any) => s.taskName.toLowerCase().includes(keyword))
    }
    if (subtaskFilters.timeRange) {
      const [start, end] = subtaskFilters.timeRange
      allSubtasks = allSubtasks.filter((s: any) => {
        if (!s.endTime) return false
        return s.endTime >= start && s.endTime <= end
      })
    }

    return allSubtasks
  }

  const renderSubtaskTree = () => {
    if (!planData) return null

    const filteredSubtasks = getFilteredSubtasks()
    // Group subtasks by milestone
    const groupedSubtasks: { [key: string]: any[] } = {}
    filteredSubtasks.forEach((subtask: any) => {
      if (!groupedSubtasks[subtask.milestoneId]) {
        groupedSubtasks[subtask.milestoneId] = []
      }
      groupedSubtasks[subtask.milestoneId].push(subtask)
    })

    const treeData = planData.milestones
      .filter((milestone: Milestone) => groupedSubtasks[milestone.milestoneId]?.length > 0)
      .map((milestone: Milestone) => ({
        title: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <Text strong>{milestone.milestoneName}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({groupedSubtasks[milestone.milestoneId]?.length || 0}{t('planDetail.subtask.taskCountSuffix')})
              </Text>
            </span>
            <StatusTag type="milestone" status={milestone.status} />
          </div>
        ),
        key: milestone.milestoneId,
        children: groupedSubtasks[milestone.milestoneId]?.map((subtask: any) => ({
          title: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 4 }}>
                  <Text>{subtask.taskName}</Text>
                  <StatusTag type="milestone" status={subtask.status} style={{ marginLeft: 8 }} />
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  <span>{t('planDetail.subtask.detail.assignee')}：{subtask.assignee}</span>
                  <Divider type="vertical" />
                  <span>{t('planDetail.subtask.detail.createTime')}：{subtask.createTime ? new Date(subtask.createTime).toLocaleDateString() : '2025/1/12'}</span>
                  <Divider type="vertical" />
                  <span>{t('planDetail.subtask.detail.endTime')}：{subtask.endTime ? new Date(subtask.endTime).toLocaleDateString() : t('planDetail.subtask.detail.noEndTime')}</span>
                  <Divider type="vertical" />
                  <span>{t('planDetail.subtask.detail.remarks')}：{subtask.remarks || t('planDetail.subtask.detail.defaultRemarks')}</span>
                </div>
                {subtask.deliverableFiles?.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {subtask.deliverableFiles.map((file: any) => (
                      <Tag 
                        key={file.fileId} 
                        icon={<FileTextOutlined />}
                        style={{ cursor: 'pointer' }}
                        onClick={() => window.open(file.downloadUrl)}
                      >
                        {file.fileName}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ),
          key: subtask.subtaskId,
          isLeaf: true
        }))
      }))

    return (
      <Tree
        treeData={treeData}
        expandedKeys={expandedKeys}
        onExpand={(expandedKeys: React.Key[]) => setExpandedKeys(expandedKeys.map(key => String(key)))}
        showLine
        showIcon={false}
      />
    )
  }

  const handleExpandAll = () => {
    if (allExpanded) {
      setExpandedKeys([])
    } else {
      const allKeys = planData?.milestones.map((m: Milestone) => m.milestoneId) || []
      setExpandedKeys(allKeys)
    }
    setAllExpanded(!allExpanded)
  }

  if (loading) {
    return (
      <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div style={{ 
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #e5e7eb',
          padding: '24px 32px'
        }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/plans')}
            style={{ 
              padding: 0, 
              height: 'auto',
              color: '#6b7280',
              fontSize: '14px'
            }}
          >
            {t('planDetail.backToList')}
          </Button>
          <Title level={2} style={{ 
            margin: 0, 
            color: '#1f2937'
          }}>
            {t('planDetail.loading')}
          </Title>
        </div>
        
        <div style={{ 
          padding: '64px 24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Spin size="large" />
        </div>
      </div>
    )
  }

  if (!planData) {
    return (
      <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div style={{ 
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #e5e7eb',
          padding: '24px 32px'
        }}>
          <Button
             type="link"
             icon={<ArrowLeftOutlined />}
             onClick={() => navigate('/plans')}
             style={{ 
               padding: 0, 
               height: 'auto',
               color: '#6b7280',
               fontSize: '14px'
             }}
           >
             {t('planDetail.backToList')}
           </Button>
          <Title level={2} style={{ 
            margin: 0, 
            color: '#1f2937'
          }}>
            {t('planDetail.planNotFound')}
          </Title>
        </div>
        
        <div style={{ 
          padding: '24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <Empty description={t('planDetail.planNotFoundOrLoadFailed')} />
        </div>
      </div>
    )
  }

  const { plan, milestones } = planData
  const milestoneProgress = calculateMilestoneProgress()
  const subtaskProgress = calculateSubtaskProgress()
  const milestoneStats = getMilestoneStatistics()

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Page Header */}
      <div style={{ 
        backgroundColor: '#fafafa', 
        borderBottom: '1px solid #e5e7eb',
        padding: '24px 32px'
      }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/plans')}
          style={{ 
            padding: 0, 
            height: 'auto',
            color: '#6b7280',
            fontSize: '14px'
          }}
        >
          {t('planDetail.backToList')}
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
          <div>
            <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
              {plan.planName}
            </Title>
            <Text type="secondary" style={{ fontSize: '14px', marginTop: '4px' }}>
              {t('planDetail.planCode')}：{plan.planCode} | {t('planDetail.templateOwner')}：{plan.templateOwner}
            </Text>
          </div>
          <Space>
            <Button
              type="primary"
              size="large"
              onClick={() => navigate(`/assignment/${plan.id}`, { state: { from: '/detail' } })}
              style={{
                borderRadius: '8px',
                backgroundColor: '#3b82f6',
                borderColor: '#3b82f6',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {plan.status === 'InProgress' ? t('planDetail.modifyPersonnel') : t('planDetail.personnelAssignment')}
            </Button>
          </Space>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '24px' }}>
        {/* Plan Header */}
        <Card 
          style={{ 
            marginBottom: 24,
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <Row gutter={24}>
            <Col span={16}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label={t('planDetail.planType')}>
                  <Tag color="blue">{plan.planType}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('planDetail.templateReference')}>{plan.templateReference}</Descriptions.Item>
                {/* <Descriptions.Item label={t('planDetail.businessActivity')}>{plan.businessActivity}</Descriptions.Item> */}
                <Descriptions.Item label={t('planDetail.currentMilestone')}>{plan.currentMilestone}</Descriptions.Item>
                <Descriptions.Item label={t('planDetail.storeScope')}>
                  <Popover 
                    content={
                      <div style={{ maxWidth: 300, wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {plan.storeScope}
                      </div>
                    } 
                    title={t('planDetail.storeScope')} 
                    trigger="hover"
                  >
                    <span style={{ cursor: 'pointer' }}>
                      {plan.storeScope && plan.storeScope.length > 20 
                        ? `${plan.storeScope.substring(0, 20)}...` 
                        : plan.storeScope}
                    </span>
                  </Popover>
                </Descriptions.Item>
                <Descriptions.Item label={t('planDetail.storeCount')}>{plan.storeCount}{t('planDetail.stores')}</Descriptions.Item>
                <Descriptions.Item label={t('planDetail.planTime')}>
                  {new Date(plan.plannedStartTime).toLocaleDateString()} - {new Date(plan.plannedEndTime).toLocaleDateString()}
                </Descriptions.Item>
                <Descriptions.Item label={t('planDetail.status')}>
                  <StatusTag type="plan" status={plan.status} />
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={8}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title={t('planDetail.milestoneProgress')}
                    value={milestoneProgress.percentage}
                    suffix="%"
                    prefix={<CheckCircleOutlined />}
                  />
                  <Text type="secondary">
                    {milestoneProgress.completed}/{milestoneProgress.total}
                  </Text>
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('planDetail.taskProgress')}
                    value={subtaskProgress.percentage}
                    suffix="%"
                    prefix={<FileTextOutlined />}
                  />
                  <Text type="secondary">
                    {subtaskProgress.completed}/{subtaskProgress.total}
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        {/* Tabs */}
        <Card
          style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            background: '#fff'
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            size="large"
            style={{ padding: '0 24px' }}
            tabBarStyle={{ 
              marginBottom: 0,
              borderBottom: '1px solid #f0f0f0'
            }}
            items={[
              {
                key: 'milestones',
                label: t('planDetail.tabs.milestoneDetails'),
                children: (
                  <div style={{ padding: '24px' }}>
                    {/* 里程碑时间线 */}
                    <Row gutter={24}>
                      <Col span={24}>
                        <Card 
                          title={t('planDetail.milestone.timeline')} 
                          size="small" 
                          style={{ 
                            marginBottom: 24,
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                          }}
                          bodyStyle={{ padding: '16px' }}
                        >
                          {renderMilestoneTimeline()}
                        </Card>
                      </Col>
                    </Row>

                    {/* 里程碑统计 */}
                    <Row gutter={24}>
                      <Col span={24}>
                        <Card 
                          title={t('planDetail.milestone.statistics')} 
                          size="small" 
                          style={{ 
                            marginBottom: 24,
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                          }}
                          bodyStyle={{ padding: '24px' }}
                        >
                          <Row gutter={32}>
                            <Col span={6}>
                              <Statistic
                                title={t('planDetail.milestone.totalMilestones')}
                                value={milestoneProgress.total}
                                prefix={<CalendarOutlined />}
                              />
                            </Col>
                            <Col span={6}>
                              <Statistic
                                title={t('planDetail.milestone.completed')}
                                value={milestoneProgress.completed}
                                valueStyle={{ color: '#52c41a' }}
                                prefix={<CheckCircleOutlined />}
                              />
                            </Col>
                            <Col span={6}>
                              <Statistic
                                title={t('planManagement.detail.milestone.inProgress')}
                                value={milestones.filter((m: Milestone) => m.status === 'InProgress').length}
                                valueStyle={{ color: '#1890ff' }}
                                prefix={<ClockCircleOutlined />}
                              />
                            </Col>
                            <Col span={6}>
                              <Statistic
                                title={t('planManagement.detail.milestone.overdue')}
                                value={milestones.filter((m: Milestone) => m.status === 'Overdue').length}
                                valueStyle={{ color: '#ff4d4f' }}
                                prefix={<WarningOutlined />}
                              />
                            </Col>
                          </Row>
                        </Card>
                      </Col>
                    </Row>

                    {/* 整体进度 */}
                    <Row gutter={24}>
                      <Col span={24}>
                        <Card 
                          title={t('planManagement.detail.milestone.overallProgress')} 
                          size="small"
                          style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                          }}
                          bodyStyle={{ padding: '24px' }}
                        >
                          <Row gutter={48}>
                            <Col span={12}>
                              <div style={{ textAlign: 'center' }}>
                                <Text strong style={{ display: 'block', marginBottom: 16, fontSize: '16px' }}>
                                  {t('planManagement.detail.milestone.milestoneProgress')}
                                </Text>
                                <div style={{ marginBottom: 16 }}>
                                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a', marginBottom: 8 }}>
                                    {milestoneProgress.percentage}%
                                  </div>
                                  <Text type="secondary" style={{ fontSize: '14px' }}>
                                    {milestoneProgress.completed}/{milestoneProgress.total} {t('planDetail.milestone.completed')}
                                  </Text>
                                </div>
                                <ProgressBar 
                                  completed={milestoneProgress.completed}
                                  total={milestoneProgress.total}
                                  strokeColor="#52c41a"
                                  size="small"
                                />
                              </div>
                            </Col>
                            <Col span={12}>
                              <div style={{ textAlign: 'center' }}>
                                <Text strong style={{ display: 'block', marginBottom: 16, fontSize: '16px' }}>
                                  {t('planManagement.detail.milestone.taskProgress')}
                                </Text>
                                <div style={{ marginBottom: 16 }}>
                                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', marginBottom: 8 }}>
                                    {subtaskProgress.percentage}%
                                  </div>
                                  <Text type="secondary" style={{ fontSize: '14px' }}>
                                    {subtaskProgress.completed}/{subtaskProgress.total} {t('planDetail.subtask.completed')}
                                  </Text>
                                </div>
                                <ProgressBar 
                                  completed={subtaskProgress.completed}
                                  total={subtaskProgress.total}
                                  strokeColor="#1890ff"
                                  size="small"
                                />
                              </div>
                            </Col>
                          </Row>
                        </Card>
                      </Col>
                    </Row>

                    {/* 里程碑列表 */}
                    <Row gutter={24}>
                      <Col span={24}>
                        <Card 
                          title={t('planDetail.milestone.list')} 
                          size="small"
                          style={{
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                          }}
                          extra={
                            <Button size="small" icon={<ReloadOutlined />}>{t('planDetail.milestone.refresh')}</Button>
                          }
                        >
                          <Table
                            dataSource={milestones}
                            columns={milestoneColumns}
                            pagination={false}
                            size="small"
                            rowKey="milestoneId"
                            scroll={{ y: 500 }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'subtasks',
                label: t('planDetail.tabs.subtaskDetails'),
                children: (
                  <div>
                    {/* 筛选条件栏 */}
                    <Card 
                      title={t('planDetail.subtask.filterConditions')} 
                      size="small" 
                      style={{ 
                        marginBottom: 24,
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}
                      extra={
                        <Button 
                          size="small"
                          onClick={() => setSubtaskFilters({
                            milestone: '',
                            assignee: '',
                            status: '',
                            timeRange: null,
                            keyword: ''
                          })}
                          style={{
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                          }}
                        >
                          {t('planDetail.subtask.clearFilter')}
                        </Button>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={4}>
                          <div>
                            <Text>{t('planDetail.subtask.milestone')}</Text>
                            <Select
                              style={{ width: '100%', marginTop: 4, borderRadius: '8px' }}
                              placeholder={t('planDetail.subtask.selectMilestone')}
                              allowClear
                              value={subtaskFilters.milestone}
                              onChange={(value: any) => setSubtaskFilters(prev => ({ ...prev, milestone: value || '' }))}
                            >
                              {milestones.map((milestone: Milestone) => (
                                <Select.Option key={milestone.milestoneId} value={milestone.milestoneId}>
                                  {milestone.milestoneName}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                        </Col>
                        <Col span={4}>
                          <div>
                            <Text>{t('planDetail.subtask.assignee')}</Text>
                            <Input
                              style={{ marginTop: 4, borderRadius: '8px' }}
                              placeholder={t('planDetail.subtask.inputAssignee')}
                              value={subtaskFilters.assignee}
                              onChange={(e: any) => setSubtaskFilters(prev => ({ ...prev, assignee: e.target.value }))}
                            />
                          </div>
                        </Col>
                        <Col span={4}>
                          <div>
                            <Text>{t('planDetail.subtask.taskStatus')}</Text>
                            <Select
                              style={{ width: '100%', marginTop: 4, borderRadius: '8px' }}
                              placeholder={t('planDetail.subtask.selectStatus')}
                              allowClear
                              value={subtaskFilters.status}
                              onChange={(value: any) => setSubtaskFilters(prev => ({ ...prev, status: value || '' }))}
                            >
                              <Select.Option value="InProgress">{t('planDetail.subtask.statuses.Ongoing')}</Select.Option>
                              <Select.Option value="Completed">{t('planDetail.subtask.statuses.Finished')}</Select.Option>
                              <Select.Option value="Skipped">{t('planDetail.subtask.statuses.Skipped')}</Select.Option>
                            </Select>
                          </div>
                        </Col>
                        <Col span={4}>
                          <div>
                            <Text>{t('planDetail.subtask.completionTime')}</Text>
                            <RangePicker
                              style={{ width: '100%', marginTop: 4, borderRadius: '8px' }}
                              value={subtaskFilters.timeRange}
                              onChange={(dates: any) => setSubtaskFilters(prev => ({ ...prev, timeRange: dates }))}
                            />
                          </div>
                        </Col>
                        <Col span={4}>
                          <div>
                            <Text>{t('planDetail.subtask.keywordSearch')}</Text>
                            <Input
                              style={{ marginTop: 4, borderRadius: '8px' }}
                              placeholder={t('planDetail.subtask.searchTaskName')}
                              prefix={<SearchOutlined />}
                              value={subtaskFilters.keyword}
                              onChange={(e: any) => setSubtaskFilters(prev => ({ ...prev, keyword: e.target.value }))}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Card>

                    <Row gutter={24}>
                      {/* 任务树结构 */}
                      <Col span={22}>
                        <Card 
                          title={t('planDetail.subtask.taskTreeStructure')} 
                          size="small"
                          style={{
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                          }}
                          extra={
                            <Space>
                              <Button 
                                size="small" 
                                icon={allExpanded ? <CompressOutlined /> : <ExpandAltOutlined />}
                                onClick={handleExpandAll}
                                style={{
                                  borderRadius: '6px',
                                  border: '1px solid #d1d5db',
                                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                                }}
                              >
                                {allExpanded ? t('planDetail.subtask.collapseAll') : t('planDetail.subtask.expandAll')}
                              </Button>

                            </Space>
                          }
                        >
                          {getFilteredSubtasks().length > 0 ? (
                            renderSubtaskTree()
                          ) : (
                            <Empty description={t('planDetail.subtask.noMatchingTasks')} />
                          )}
                        </Card>
                      </Col>
                      
                      {/* 任务统计 - 紧凑版 */}
                      <Col span={2}>
                        <Card 
                          title={<span style={{ fontSize: '12px' }}>{t('planDetail.subtask.statistics')}</span>}
                          size="small"
                          style={{
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                          }}
                          bodyStyle={{ padding: '12px 8px' }}
                        >
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
                                {subtaskProgress.total}
                              </div>
                              <div style={{ fontSize: '10px', color: '#666' }}>{t('planDetail.subtask.total')}</div>
                            </div>
                            
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                                {subtaskProgress.completed}
                              </div>
                              <div style={{ fontSize: '10px', color: '#666' }}>{t('planDetail.subtask.completed')}</div>
                            </div>
                            
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                {getFilteredSubtasks().filter((s: any) => s.status === 'InProgress').length}
                              </div>
                              <div style={{ fontSize: '10px', color: '#666' }}>{t('planDetail.subtask.inProgress')}</div>
                            </div>
                            
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                                {getFilteredSubtasks().filter((s: any) => s.status === 'Cancelled').length}
                              </div>
                              <div style={{ fontSize: '10px', color: '#666' }}>{t('planDetail.subtask.canceled')}</div>
                            </div>
                            
                            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: subtaskProgress.percentage >= 80 ? '#52c41a' : '#faad14' }}>
                                {subtaskProgress.percentage}%
                              </div>
                              <div style={{ fontSize: '10px', color: '#666' }}>{t('planDetail.progress.completionRate')}</div>
                              <Progress 
                                percent={subtaskProgress.percentage}
                                strokeColor={subtaskProgress.percentage >= 80 ? '#52c41a' : '#faad14'}
                                size="small"
                                style={{ marginTop: 4 }}
                                showInfo={false}
                              />
                            </div>
                          </div>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )
              }
            ]}
          />
        </Card>
      </div>
    </div>
  )
}

export default PlanDetail